import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, Stack, CircularProgress } from "@mui/material";
import Page from "src/components/Page";
import { BackButton } from "src/common/components/BackButton";
import {
  cmCustomerTabList,
  cmWalletHistoryColumns,
  cmTransactionsListColumns
} from "./user.constant";
import { useListWithCompanyId } from "src/hooks/useFetch.hook";
import { FEATURE } from "src/util/enums";
import { useSelector } from "react-redux";
import { useAccess } from "src/hooks/useAccess.hook";
import { TabContext, TabPanel, TabList } from "@mui/lab";
import { RootTabItemStyles } from "src/theme/overrides/Tabs";
import { styled } from "@mui/material/styles";
import { BaseTable } from "../BaseTable/BaseTable";
import CustomerInfoPanel from "./CustomerInfoPanel";
import SentPromoCodesPanel from "./PromoCodesPanel";
import UsedPromoCodesPanel from "./UsedPromoCodesPanel";
import InvoiceTemplate from "./InvoiceTemplate";
import axios from "axios";
import { generateTransactionPDFFromHTML } from "./TransactionPdf";

const RootTabStyle = styled(TabList)(({ theme, length }) => ({
  width: "98%",
  borderTopLeftRadius: theme.spacing(1),
  borderTopRightRadius: theme.spacing(1),
  "& .MuiTabs-flexContainer": {
    justifyContent: "left",
  },
  "& .MuiTab-root": {
    width: `${100 / length}%`,
  },
}));

const CMCustomerWalletTransactionHistory = () => {
  const { id, companyId } = useParams();
  const { teamId } = useSelector((state) => state.teams);
  const { data: customers, isFetching } = useListWithCompanyId("cmWalletHistory", true, companyId, id);
  const { data: customerDetails, isFetching: isCustomerFetchin } = useListWithCompanyId("cmUserDetails", true, companyId, id);
  const { data: transactions, isFetching: isTransactionFetching } = useListWithCompanyId("cmCustomerTransactionHistory", true, companyId,id);
  const { data: promoCodes, isFetching: isPromoCodesFetching } = useListWithCompanyId("getPromoCodes", true, companyId, id);
  const [unUsedPromoCodes, setUnUsedPromoCodes] = useState([]);
  const [usedPromoCodes, setUsedPromoCodes] = useState([]);
  const isCustomerFetching = false;
  const isReadOnly = useAccess(FEATURE.CUSTOMERS, "R");
  const [selectedTab, setSelectedTab] = useState(cmCustomerTabList[0].value);
  const invoiceRef = useRef();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [currentDownloadingId, setCurrentDownloadingId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };
  useEffect(() => {
    setUnUsedPromoCodes(Array.isArray(promoCodes?.sentPromoCodes) && promoCodes?.sentPromoCodes || []);
    setUsedPromoCodes(Array.isArray(promoCodes?.usedPromoCodes) && promoCodes?.usedPromoCodes || []);
  }, [promoCodes]);

  // downloadInvoice - Updated to use HTML-to-PDF with loading state
  const downloadInvoice = async (row) => {
    if (isGeneratingPDF) {
      return; // Prevent multiple clicks
    }
    setIsGeneratingPDF(true);
    setCurrentDownloadingId(row?.transactionId);
    
    // Add a small delay to ensure state update is visible
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const fullUrl = `${process.env.REACT_APP_CSMS_NEST_ENDPOINT}/invoice/send/evdriver`;
      const invoiceData = await axios.post(fullUrl, {
        transactionId: row?.transactionId,
        ...row?.email && { email: row?.email },
        getPdf: true
      }, {
        headers: { 
          'Content-Type': 'application/json',
        }
      });
      
      // Try different ways to access the data based on API structure
      const content = invoiceData?.data?.data?.template_content || invoiceData?.data?.template_content;
      const tx = content?.replace(/\n/g, "");
      
      if (!tx) {
        console.error("No transaction data returned from API");
        return;
      }
      
      // Set transaction data for the invoice template with GST option
      setSelectedTransaction(tx);
      
      // Wait longer for the component to render and images to convert, then generate PDF
      setTimeout(async () => {
        try {
          await generateTransactionPDFFromHTML(invoiceRef, row?.transactionId);
        } catch (error) {
          console.error("Error generating PDF from HTML:", error);
          // Fallback to original PDF generation if HTML method fails
        } finally {
          setIsGeneratingPDF(false);
          setCurrentDownloadingId(null);
        }
      }, 2000); // Increased timeout to 2 seconds for image conversion
      
    } catch (err) {
      console.error("Error generating PDF:", err);
      setIsGeneratingPDF(false);
      setCurrentDownloadingId(null);
    }
  };

  // Memoize the columns to ensure proper re-rendering when loading states change
  const transactionColumns = useMemo(() => {
    return cmTransactionsListColumns(isReadOnly, id, downloadInvoice, isGeneratingPDF, currentDownloadingId);
  }, [isReadOnly, id, downloadInvoice, isGeneratingPDF, currentDownloadingId]);

  return (
    <Page title="Customer | Zynka - CMS">
      <BackButton path="/cm-customer" text="Customer Details" />
      <TabContext value={selectedTab}>
        <Stack direction="row" justifyContent="center" alignItem="center" sx={{ width: "100%" }}>
          <RootTabStyle
            indicatorColor="transparent"
            centered
            length={cmCustomerTabList.length}
            onChange={handleTabChange}
          >
            {cmCustomerTabList.map((tab) => (
              <RootTabItemStyles key={tab.value} value={tab.value} label={tab.label} />
            ))}
          </RootTabStyle>
        </Stack>
        <Card sx={{ p: 4 }}>
          <TabPanel value="customer_info">
            <CustomerInfoPanel
              customerData={customerDetails?.[0]}
              isLoading={isCustomerFetching}
            />
          </TabPanel>
          <TabPanel value="wallet_history">
            <BaseTable
              rows={customers ?? []}
              columns={cmWalletHistoryColumns(isReadOnly)}
              loading={isFetching}
              getRowId={(row) => row.id}
              pageSize={10}
            />
          </TabPanel>
          <TabPanel value="transaction_history">
            <div style={{ position: 'relative' }}>
              {isGeneratingPDF && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  borderRadius: '8px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    padding: '16px 24px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                  }}>
                    <CircularProgress size={24} />
                    <span style={{ fontSize: '14px', color: '#666' }}>Generating PDF...</span>
                  </div>
                </div>
              )}
              <BaseTable
                rows={transactions ?? []}
                columns={transactionColumns}
                loading={isTransactionFetching}
                getRowId={(row) => row.transactionId}
                pageSize={paginationModel.pageSize}
                paginationModel={paginationModel}
                onPageChange={(page) => setPaginationModel(prev => ({ ...prev, page }))}
                onPageSizeChange={(pageSize) => setPaginationModel(prev => ({ ...prev, pageSize, page: 0 }))}
              />
            </div>
          </TabPanel>
          <TabPanel value="sent_promo_codes">
            <SentPromoCodesPanel isReadOnly={isReadOnly} data={unUsedPromoCodes} />
          </TabPanel>
          <TabPanel value="used_promo_codes">
            <UsedPromoCodesPanel isReadOnly={isReadOnly} data={usedPromoCodes} />
          </TabPanel>
        </Card>
      </TabContext>
      
      {/* Hidden Invoice Template for PDF generation */}
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <InvoiceTemplate
          ref={invoiceRef}
          transactionData={selectedTransaction}
        />
      </div>
    </Page>
  );
};

export default CMCustomerWalletTransactionHistory;
